import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from "react";
import { apiSetRating } from "../api/client";
import { parseSections, isSubstantive } from "../util/parseDescription";
// Full description rendering for StudioView's player column. Mirrors
// EventCard's section layout but with the wireframe's larger spacing,
// uppercase section labels (#a8a8a8 per D11 for AA contrast), and accent
// color on the EVENTS section to call out the actionable line.
//
// Rating bar is colocated here (rather than on the player) because the
// rating is about the description's value to the operator, not the clip.
export default function DescriptionPanel({ ev, dimmed = false }) {
    const [useful, setUseful] = useState(null);
    // Reset when the parent switches event.
    useEffect(() => {
        setUseful(ev?.sUseful ?? null);
    }, [ev]);
    if (!ev)
        return null;
    // SCENE is great for embedding/search recall but adds visual noise to the
    // operator view — strip it. Then drop any PEOPLE/EVENTS/NOTES section
    // whose body is a "no signal" stub ("No people visible.", "No notable
    // events.", "Nothing.") via the shared isSubstantive() check.
    const sections = parseSections(ev.sDescription || "")
        .filter(s => s.label !== "SCENE")
        .filter(s => isSubstantive(s.text));
    const setRating = async (r) => {
        // Capture (customer, bID) at click-time. If the user switches event
        // before the rating round-trip resolves, we'd otherwise stamp ev=A's
        // success onto ev=B's local state — visually telling the operator they
        // rated B while the server actually has the rating against A. Compare
        // against the CURRENT ev at resolve time to drop the late update.
        const targetCustomer = ev.customer;
        const targetBid = ev.bID;
        try {
            await apiSetRating(targetCustomer, targetBid, r);
            if (ev.customer === targetCustomer && ev.bID === targetBid) {
                setUseful(r === "useful" ? "yes" : r === "not_useful" ? "no" : null);
            }
        }
        catch (e) {
            console.error("set rating failed", e);
        }
    };
    return (_jsxs("div", { className: dimmed ? "description-panel description-panel-dimmed" : "description-panel", style: dimmed ? { opacity: 0.6 } : undefined, children: [_jsxs("div", { className: "clip-meta", children: [_jsx("span", { children: new Date(ev.tEventStart).toLocaleString() }), _jsxs("span", { children: ["cam ", ev.bCamera] }), _jsx("span", { children: ev.customer }), typeof ev.distance === "number" && (_jsx("span", { className: "event-score", children: ev.distance.toFixed(3) }))] }), sections.length === 0 && ev.sDescription && (_jsx("div", { className: "section", children: _jsx("span", { className: "section-text", children: ev.sDescription }) })), sections.map(s => (_jsxs("div", { className: `section section-${s.label.toLowerCase()}`, children: [_jsx("span", { className: "section-label", children: s.label.toLowerCase() }), _jsx("span", { className: "section-text", children: s.text })] }, s.label))), _jsxs("div", { className: "rating-bar", children: [_jsx("button", { className: useful === "yes" ? "rating active" : "rating", onClick: () => setRating("useful"), "aria-pressed": useful === "yes", children: "\uD83D\uDC4D useful" }), _jsx("button", { className: useful === "no" ? "rating active" : "rating", onClick: () => setRating("not_useful"), "aria-pressed": useful === "no", children: "\uD83D\uDC4E not useful" }), useful !== null && (_jsx("button", { className: "rating ghost", onClick: () => setRating("unrated"), children: "clear" }))] })] }));
}
